// 3rd-party modules
// import ReactGA from 'react-ga4';
import { Outlet } from 'react-router-dom';
import { ReactNode, createContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

// project modules
import FooterNavigation from '../components/global/footerNavigation';
import StickyElement from '../components/shared/stickystuck';
import TopNavigation from '../components/global/topNavigation';
import { apiCall } from '../helpers/apiHelper';
import { RootState } from '../stores';

// apis
import * as PostApi from '../apis/postApi';

// models
import { Post } from '../models/post';
import { TopNavigationItem } from '../models/types/navigation';
import { User } from '../models/auth';

// defines
type Props = {
  children?: ReactNode
}

/*
const topNavigationItems: TopNavigationItem[] = [
  {
    text: "Home",
    link: "/",
  },
  {
    text: "Product",
    link: "/product",
  },
  {
    text: "Futures",
    link: "/futures",
  },
  {
    text: "Pricing",
    link: "/pricing",
  },
  {
    text: "Reviews",
    link: "/reviews",
  },
  {
    text: "FAQ",
    link: "/faq",
  },
  {
    text: "Login",
    link: "/auth/login",
  },
];
*/

const getPages = async (language: string, abortSignal?: AbortSignal) => {
  const response = await apiCall(PostApi.getPages(language, abortSignal));

  return response.success ? Post.toArrayOfClass(response.data?.value || []) : [];
};

const getPagePosts = async (parentId: number, language: string, abortSignal?: AbortSignal) => {
  const response = await apiCall(PostApi.getPagePosts(parentId, language, abortSignal));

  return response.success ? Post.toArrayOfClass(response.data?.value || []) : [];
};

export default function DefaultLayout({ children }: Props) {
  const [pages, setPages] = useState<Post[]>([]);
  const [pagePosts, setPagePosts] = useState<Post[]>([]);
  const [topNavigationItems, setTopNavigationItems] = useState<TopNavigationItem[]>([]);
  const language = useSelector((state: RootState) => state.user.language);
  const user = useSelector((state: RootState) => state.user.currentUser) as User;
  const abortController = new AbortController();

  /*
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }, []);
  */

  // useEffect(() => {
  //   const getPagesAsync = async () => {
  //     setPages(await getPages(language, abortController.signal));
  //   }

  //   getPagesAsync();
  // }, [language]);

  useEffect(() => {
    let topNavigationItems = [...(pages.map(p => { return { id: p.postId?.toString(), text: p.title, link: p.routePath, children: [] } }) as TopNavigationItem[])];

    // if user has not signed-in, use login nav-item else portal
    if (user && user.email && !user.lockoutEnabled)
      topNavigationItems = topNavigationItems.filter(navItem => navItem.link !== '/auth/login');
    else
      topNavigationItems = topNavigationItems.filter(navItem => navItem.link !== '/portal');

    setTopNavigationItems(topNavigationItems);
  }, [pages]);

  const onNavItemSelect = async (navItem: TopNavigationItem) => {
    setPagePosts(await getPagePosts(Number(navItem.id), language, abortController.signal));
  };

  return (
    <>
      <main>
        <space></space>
        <StickyElement>
          <TopNavigation navItems={topNavigationItems} onNavItemSelect={onNavItemSelect} />
        </StickyElement>
        <space></space>
        <PostContext.Provider value={pagePosts}>
          {children}
          <Outlet />
        </PostContext.Provider>
        <footer data-type="group">
          <section>
            <wrapper>
              <group data-space-horizontal="10">
                <separator horizontal=""></separator>
                <space></space>
                <group data-align="center" data-gap="40" data-wrap="wrap">
                  <group data-width="auto" data-gap="20" data-wrap="no" data-position="left">
                    <group data-width="auto" data-test="">
                      <svg
                        xmlns="http://www.w3.org/2000/svg" width="45px" height="45px"
                        viewBox="0 0 335 335">
                        <path d="M249.9,311.7l-86.3-121.9l-10.7,11v42.1h-31.3v-91.7V40c-52,18.8-89.3,68.7-89.3,127.1
                                c0,74.5,60.6,135.2,135.2,135.2c11.9,0,23.5-1.6,34.5-4.5l19,26.9c-16.8,5.7-34.7,8.8-53.4,8.8c-91.8,0-166.4-74.7-166.4-166.4
                                C1.1,80.3,68,8.8,152.9,1.4l-0.1,154.4l46.5-47.9l22.4,21.8L185.7,167L258,267.4c27.4-24.8,44.7-60.5,44.7-100.3
                                c0-68.9-51.8-125.9-118.5-134.1V1.6c84,8.4,149.8,79.4,149.8,165.6C333.9,229,300,283,249.9,311.7z"/>
                      </svg>
                    </group>
                    <group data-width="auto" data-gap="10">
                      <a data-type="group" href="/">
                        <logo data-shrink="0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="150" height="20" viewBox="0 0 173.91 17.531">
                            <path d="M5319.46,360.439h-1.7l-8.19,7.824v-7.824h-1.27v16.8h1.27v-8.28l8.43,8.28h1.7l-8.73-8.64Zm23.04,16.8v-16.8h-1.27v16.8h1.27Z" transform="translate(-5308.31 -359.938)"></path>
                            <path data-accent="" data-fill="main-color" d="M5376.82,365.076a4.357,4.357,0,0,0-1.5-3.492,6.02,6.02,0,0,0-4.05-1.284h-6.62v16.8h7.08a6.067,6.067,0,0,0,4.1-1.344,4.456,4.456,0,0,0,1.54-3.576,4.107,4.107,0,0,0-2.11-3.768,4.012,4.012,0,0,0,1.56-3.336h0Zm-4.68-.576a1.418,1.418,0,0,1,0,1.932,1.431,1.431,0,0,1-.99.348h-2.21v-2.64h2.21A1.4,1.4,0,0,1,5372.14,364.5Zm-3.2,5.928h2.67a1.464,1.464,0,0,1,1.05.384,1.489,1.489,0,0,1,0,2.052,1.438,1.438,0,0,1-1.05.4h-2.67v-2.832Zm46.32-1.728a8.366,8.366,0,0,0-2.56-6.216,8.962,8.962,0,0,0-12.51,0,8.905,8.905,0,0,0,0,12.456,8.815,8.815,0,0,0,15.07-6.24h0Zm-13.34,0a4.307,4.307,0,0,1,1.3-3.264,4.7,4.7,0,0,1,6.48,0,4.337,4.337,0,0,1,1.29,3.264,4.466,4.466,0,0,1-1.29,3.288,4.525,4.525,0,0,1-3.24,1.272,4.4,4.4,0,0,1-4.54-4.56h0Zm44.5,7.368a4.68,4.68,0,0,0,1.75-3.864,4.089,4.089,0,0,0-1.75-3.672,9.752,9.752,0,0,0-1.64-.912c-0.46-.192-1.12-0.416-1.96-0.672a9.9,9.9,0,0,1-2.3-.888,1.1,1.1,0,0,1-.58-0.936,0.946,0.946,0,0,1,.4-0.8,1.931,1.931,0,0,1,1.16-.3,2.7,2.7,0,0,1,2.61,1.872l3.6-2.112a6.425,6.425,0,0,0-2.49-2.844,7.063,7.063,0,0,0-3.75-1,6.22,6.22,0,0,0-4.17,1.44,4.739,4.739,0,0,0-1.66,3.768,4.421,4.421,0,0,0,1.27,3.312,8.6,8.6,0,0,0,3.72,1.944c1.15,0.336,1.86.552,2.11,0.648a1.466,1.466,0,0,1,1.13,1.2c0,0.752-.66,1.128-1.97,1.128a3.341,3.341,0,0,1-3.31-2.232l-3.67,2.136a5.95,5.95,0,0,0,2.48,3.072,8.128,8.128,0,0,0,4.38,1.1A7.258,7.258,0,0,0,5446.42,376.068Zm26.8-9.552V360.3h-4.29v16.8h4.29v-6.456h4.73V377.1h4.27V360.3h-4.27v6.216h-4.73Z" transform="translate(-5308.31 -359.938)"></path>
                          </svg>
                        </logo>
                      </a>
                      <text data-text-size="12" data-weight="700" data-wrap="wrap">Copyright {moment().format("YYYY")} Kibosh LLC, all rights reserved.</text>
                    </group>
                  </group>
                </group>
                <space></space>
                <space mini=""></space>
              </group>
            </wrapper>
          </section>
        </footer>
      </main>
    </>
  );
}

export const PostContext = createContext<Post[]>([]);
